import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './MonitGPS.css';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Elearning = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    confirmed: false
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.confirmed) {
      setErrorMessage('Musisz potwierdzić zapoznanie się z materiałem, aby kontynuować.');
      return;
    }
    
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/save_elearning.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', surname: '', email: '', confirmed: false });
      } else {
        setStatus('error');
        setErrorMessage('Wystąpił błąd podczas zapisywania danych. Prawdopodobnie serwer nie ma ustawionych uprawnień (CHMOD) do utworzenia i zapisu pliku.');
      }
    } catch (error) {
      // It will throw an error locally on npm start because it's looking for PHP, which React dev server doesn't support.
      // But it will work on the Apache production server.
      setStatus('error');
      setErrorMessage('Połączenie nie powiodło się. Upewnij się, że strona działa na serwerze z obsługą PHP.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img src="/images/logominitgps.png" alt="MonitGPS Logo" className="h-12 w-auto cursor-pointer" />
            </a>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-cyan-400 transition-colors">Strona Główna</a>
            <a href="/Zawodnik" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">Strona Zawodnika</a>
          </div>
        </div>
      </motion.nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 flex items-center justify-center overflow-hidden bg-carbon min-h-[40vh]">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <motion.div 
          className="absolute top-1/2 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.h1 
            className="font-display font-black text-5xl md:text-6xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Szkolenie <span className="text-gradient">E-learningowe</span>
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Prosimy o zapoznanie się z poniższym materiałem edukacyjnym, a następnie wypełnienie formularza potwierdzającego, który jest wymagany.
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* Content & Form */}
      <section className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          
          {/* Material to Read */}
          <ScrollReveal delay={0.1}>
            <div className="glass-effect rounded-3xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              
              <div className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-cyan-500/30 flex flex-col items-center justify-center bg-cyan-500/5 mb-8 relative z-10 p-6">
                <span className="text-6xl mb-6">📖</span>
                <span className="text-cyan-400 font-display font-bold text-2xl">Materiał Szkoleniowy PDF</span>
                <p className="text-sm text-gray-400 mt-4 leading-relaxed">Pobierz lub wyświetl dokumentację, w której opisujemy kluczowe instrukcje instalacji, zasady bezpieczeństwa oraz odpowiedzialności na trasie.</p>
              </div>
              <a 
                href="/downloads/elearning.pdf" 
                download 
                target="_blank"
                className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl font-display font-bold text-white hover:bg-white/10 hover:border-cyan-500/50 transition-all flex items-center justify-center space-x-2 relative z-10"
              >
                <span>Otwórz / Pobierz PDF</span>
                <span className="text-xl">⬇</span>
              </a>
            </div>
          </ScrollReveal>

          {/* Form */}
          <ScrollReveal delay={0.3}>
            <div className="glass-effect rounded-3xl p-8 relative shadow-2xl shadow-cyan-500/10">
              <h3 className="font-display font-bold text-2xl mb-8 text-white text-center">
                Potwierdzenie Szkolenia
              </h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center py-12 space-y-4">
                  <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-green-400 text-4xl mb-4">✓</div>
                  <h4 className="text-3xl font-bold font-display text-white">Dziękujemy!</h4>
                  <p className="text-gray-400 mt-2">Twoje oświadczenie o zapoznaniu się ze szkoleniem zostało bezpiecznie zapisane w systemie.</p>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Imię</label>
                      <input 
                        type="text" 
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white" 
                        placeholder="Jan"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Nazwisko</label>
                      <input 
                        type="text" 
                        name="surname"
                        required
                        value={formData.surname}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white" 
                        placeholder="Kowalski"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Adres Email</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white" 
                      placeholder="jan.kowalski@email.com"
                    />
                  </div>
                  
                  <div className="pt-6 border-t border-white/10 mt-6">
                    <label className="flex items-start space-x-4 cursor-pointer group">
                      <div className="flex items-center h-6 pt-1">
                        <input 
                          type="checkbox" 
                          name="confirmed"
                          required
                          checked={formData.confirmed}
                          onChange={handleInputChange}
                          className="w-6 h-6 rounded border-gray-600 bg-white/10 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900 accent-cyan-500"
                        />
                      </div>
                      <span className="text-sm text-gray-400 group-hover:text-white transition-colors leading-relaxed">
                        Oświadczam, że przeczytałem/am i zrozumiałem/am treść materiału PDF ze szkoleniem. Potwierdzam zgodność wprowadzonych danych i akceptuję zasady dotyczące zawodników.
                      </span>
                    </label>
                  </div>

                  {errorMessage && (
                    <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-sm font-medium">
                      {errorMessage}
                    </div>
                  )}

                  <button 
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 mt-6 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{status === 'loading' ? 'Przetwarzanie...' : 'Zatwierdź Oświadczenie'}</span>
                  </button>
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Elearning;
